// frontend/src/components/client/ClientFuelRequests.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Fuel, Calendar, X, RefreshCw, MapPin, Building2, Globe } from 'lucide-react';
import {
  getFuelRequests,
  createFuelRequest,
  cancelFuelRequest,
  getMyAircraft,
  getAeroTrickAirports,
} from '../../services/clientApi';
import airportsData from '../../data/airports.json';
import FuelRequestDrawer from './FuelRequestDrawer';
import { useClientAuth } from '../../contexts/ClientAuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const AIRPORTS_MAP = airportsData.airports;

const lookupIcao = (icao) => {
  if (!icao) return null;
  return AIRPORTS_MAP[icao.toUpperCase()] || null;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ─── Badges ───────────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const config = {
    pending:   { label: 'Pending',   color: 'bg-yellow-500/20 text-yellow-400' },
    approved:  { label: 'Approved',  color: 'bg-green-500/20 text-green-400'  },
    completed: { label: 'Completed', color: 'bg-blue-500/20 text-blue-400'    },
    rejected:  { label: 'Rejected',  color: 'bg-red-500/20 text-red-400'      },
    cancelled: { label: 'Cancelled', color: 'bg-gray-500/20 text-gray-400'    },
  };
  const { label, color } = config[status] || config.pending;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>;
};

const SubtypeBadge = ({ subtype }) => {
  if (!subtype || subtype === 'onsite') return null;
  const config = {
    away_internal: { label: 'Away · AeroTrick', color: 'bg-purple-500/20 text-purple-400' },
    away_external: { label: 'Away · External',  color: 'bg-cyan-500/20 text-cyan-400'     },
  };
  const cfg = config[subtype];
  if (!cfg) return null;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
};

// ─── ICAO Autocomplete Input ───────────────────────────────────────────────

const IcaoInput = ({ value, onChange, placeholder = 'e.g., KDFW', required = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const handleChange = (e) => {
    const raw = e.target.value.toUpperCase().trim();
    onChange(raw);
    if (raw.length >= 2) {
      const matches = Object.entries(AIRPORTS_MAP)
        .filter(([code, info]) =>
          code.startsWith(raw) ||
          info.name.toUpperCase().includes(raw) ||
          info.city.toUpperCase().includes(raw)
        )
        .slice(0, 6)
        .map(([code, info]) => ({ code, ...info }));
      setSuggestions(matches);
      setOpen(matches.length > 0);
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleSelect = (code) => {
    onChange(code);
    setSuggestions([]);
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const info = lookupIcao(value);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => value.length >= 2 && setOpen(suggestions.length > 0)}
        placeholder={placeholder}
        maxLength={4}
        required={required}
        className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base uppercase tracking-widest placeholder:normal-case placeholder:tracking-normal"
      />
      {info && (
        <p className="mt-1 text-xs text-green-400 flex items-center gap-1">
          <MapPin className="w-3 h-3 flex-shrink-0" />
          {info.name} — {info.city}, {info.state}
        </p>
      )}
      {value && value.length === 4 && !info && (
        <p className="mt-1 text-xs text-gray-500">Unknown ICAO — will be processed manually</p>
      )}
      {open && (
        <ul className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
          {suggestions.map((s) => (
            <li
              key={s.code}
              onMouseDown={() => handleSelect(s.code)}
              className="px-3 py-2.5 hover:bg-gray-700 cursor-pointer flex items-center gap-3"
            >
              <span className="text-white font-mono font-semibold text-sm w-12 flex-shrink-0">{s.code}</span>
              <span className="text-gray-300 text-sm truncate">{s.name}</span>
              <span className="text-gray-500 text-xs flex-shrink-0 ml-auto">{s.city}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────

const ClientFuelRequests = () => {
  const { client } = useClientAuth();

  const [requests, setRequests]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [showModal, setShowModal]       = useState(false);
  const [aircraftList, setAircraftList] = useState([]);
  const [fboList, setFboList]           = useState([]);
  const [submitting, setSubmitting]     = useState(false);
  
  // Drawer state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedAircraft, setSelectedAircraft] = useState(null);

  const [formData, setFormData] = useState({
    aircraft_id:      '',
    quantity:         '',
    scheduled_date:   '',
    description:      '',
    request_subtype:  'onsite',
    destination_icao: '',
  });

  const intervalRef = useRef(null);

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadRequests = useCallback(async (showRefreshAnimation = false) => {
    if (showRefreshAnimation) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await getFuelRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading fuel requests:', error);
    } finally {
      setLoading(false);
      if (showRefreshAnimation) setRefreshing(false);
    }
  }, []);

  const loadAircraft = async () => {
    try {
      const response = await getMyAircraft();
      setAircraftList(response.data);
    } catch (error) {
      console.error('Error loading aircraft:', error);
    }
  };

  const loadFboList = async () => {
    try {
      const response = await getAeroTrickAirports();
      setFboList(response.data);
    } catch (error) {
      console.error('Error loading AeroTrick airports:', error);
    }
  };

  useEffect(() => {
    loadRequests();
    loadAircraft();
    loadFboList();
    intervalRef.current = setInterval(() => loadRequests(), 60000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [loadRequests]);

  // ── Reset modal ────────────────────────────────────────────────────────────

  const resetModal = () => {
    setFormData({
      aircraft_id: '', quantity: '', scheduled_date: '',
      description: '', request_subtype: 'onsite', destination_icao: '',
    });
    setShowModal(false);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { aircraft_id, quantity, scheduled_date, request_subtype, destination_icao } = formData;

    if (!aircraft_id || !quantity || !scheduled_date) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Pour away requests, destination est obligatoire
    if (request_subtype !== 'onsite' && !destination_icao) {
      alert('Please enter a destination ICAO code');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        aircraft_id,
        quantity: parseInt(quantity, 10),
        scheduled_date,
        description: formData.description || '',
        request_subtype,
        destination_icao: destination_icao || '',
      };
      
      const response = await createFuelRequest(payload);
      const newRequestId = response.data.id;
      
      await loadRequests();
      
      // Ouvrir le drawer automatiquement après création
      const updatedRequests = await getFuelRequests();
      const newRequest = updatedRequests.data.find(r => r.id === newRequestId);
      const aircraft = aircraftList.find(a => a.id === aircraft_id);
      
      if (newRequest && aircraft) {
        setSelectedRequest(newRequest);
        setSelectedAircraft(aircraft);
      }
      
      resetModal();
    } catch (error) {
      console.error('Error creating fuel request:', error);
      alert(JSON.stringify(error?.response?.data));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Cancel ─────────────────────────────────────────────────────────────────

  const handleCancel = async (id) => {
    if (window.confirm('Cancel this request?')) {
      try {
        await cancelFuelRequest(id);
        await loadRequests();
      } catch {
        alert('Error cancelling request');
      }
    }
  };

  // ── Subtype helpers ────────────────────────────────────────────────────────

  const handleSubtypeChange = (subtype) => {
    setFormData(prev => ({
      ...prev,
      request_subtype: subtype,
      destination_icao: '',
    }));
  };

  // ── Drawer handlers ────────────────────────────────────────────────────────
  
  const handleRequestClick = (request) => {
    const aircraft = aircraftList.find(a => a.id === request.aircraft_id);
    if (aircraft) {
      setSelectedAircraft(aircraft);
      setSelectedRequest(request);
    }
  };

  const handleDrawerClose = () => {
    setSelectedRequest(null);
    setSelectedAircraft(null);
    loadRequests();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="px-4 sm:px-0">

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 mr-3">
          <h2 className="text-xl font-bold text-white">Fuel Requests</h2>
          <p className="text-gray-400 text-sm">Manage your fuel requests</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => loadRequests(true)}
            disabled={refreshing}
            className="p-2.5 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-lg text-white transition disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="p-2.5 sm:px-4 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-lg text-white transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Request</span>
          </button>
        </div>
      </div>

      <div className="text-right mb-3">
        <span className="text-xs text-gray-500">Auto-refresh every 60s</span>
      </div>

      {/* Liste des demandes */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <Fuel className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No fuel requests</p>
          <button onClick={() => setShowModal(true)} className="mt-3 text-blue-400 hover:text-blue-300 text-sm active:opacity-70">
            Create a request
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const destInfo = lookupIcao(req.destination_icao);
            return (
              <div
                key={req.id}
                onClick={() => handleRequestClick(req)}
                className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 cursor-pointer hover:bg-gray-800/70 active:bg-gray-700/60 transition-all duration-200"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg flex-shrink-0">
                    <Fuel className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className="text-white font-medium">{req.quantity} gal</span>
                        <StatusBadge status={req.status} />
                        <SubtypeBadge subtype={req.request_subtype} />
                      </div>
                      {req.status === 'pending' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancel(req.id);
                          }}
                          className="p-1.5 hover:bg-red-500/20 active:bg-red-500/30 rounded-lg text-red-400 transition flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <p className="text-sm text-gray-400 mt-1 truncate">
                      {req.tail_number} – {req.manufacturer} {req.model}
                    </p>

                    {req.destination_icao && (
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <MapPin className="w-3 h-3 text-gray-500 flex-shrink-0" />
                        <span className="text-xs font-mono text-white">{req.destination_icao}</span>
                        {destInfo && (
                          <span className="text-xs text-gray-500 truncate">— {destInfo.name}, {destInfo.city}</span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3 flex-shrink-0" />
                      <span>{formatDate(req.scheduled_date)}</span>
                    </div>

                    {req.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{req.description}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FAB mobile */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 sm:hidden w-14 h-14 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-full text-white shadow-lg flex items-center justify-center transition z-40"
        style={{ boxShadow: '0 4px 20px rgba(59,130,246,0.5)' }}
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* DRAWER */}
      {selectedRequest && selectedAircraft && (
        <FuelRequestDrawer
          request={selectedRequest}
          aircraft={selectedAircraft}
          client={client}
          onClose={handleDrawerClose}
          onRefresh={loadRequests}
        />
      )}

      {/* MODAL de création */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50">
          <div className="bg-gray-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-md border border-gray-700 max-h-[92vh] overflow-y-auto">

            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-gray-600 rounded-full" />
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <h3 className="text-white font-semibold">New Fuel Request</h3>
              <button onClick={resetModal} className="text-gray-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">

              {/* Toggle On-site / Away */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Request Type *</label>
                <div className="grid grid-cols-3 gap-1.5 bg-gray-900 rounded-xl p-1">
                  {[
                    { value: 'onsite',        label: 'On-site',  Icon: Building2 },
                    { value: 'away_internal', label: 'Away · AeroTrick', Icon: Fuel },
                    { value: 'away_external', label: 'Away · External',  Icon: Globe },
                  ].map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleSubtypeChange(value)}
                      className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-lg text-xs font-medium transition ${
                        formData.request_subtype === value
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="leading-tight text-center">{label}</span>
                    </button>
                  ))}
                </div>

                <p className="mt-2 text-xs text-gray-500">
                  {formData.request_subtype === 'onsite' && 'Fuel delivery at your home FBO.'}
                  {formData.request_subtype === 'away_internal' && 'Fuel at another AeroTrick-managed FBO.'}
                  {formData.request_subtype === 'away_external' && 'Fuel at any airport. Our team will coordinate.'}
                </p>
              </div>

              {/* Destination ICAO - visible pour TOUS les types */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  Destination ICAO {formData.request_subtype !== 'onsite' && '*'}
                </label>
                
                {formData.request_subtype === 'away_internal' ? (
                  <select
                    value={formData.destination_icao}
                    onChange={(e) => setFormData({ ...formData, destination_icao: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                    required
                  >
                    <option value="">Select a FBO</option>
                    {fboList.map((fbo) => (
                      <option key={fbo.id} value={fbo.icao_code}>
                        {fbo.icao_code} — {fbo.name}{fbo.city ? `, ${fbo.city}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <IcaoInput
                    value={formData.destination_icao}
                    onChange={(val) => setFormData({ ...formData, destination_icao: val })}
                    placeholder={formData.request_subtype === 'onsite' ? "e.g., KDFW (optional)" : "e.g., KDFW"}
                    required={formData.request_subtype !== 'onsite'}
                  />
                )}
                
                {formData.request_subtype === 'onsite' && (
                  <p className="mt-1 text-xs text-gray-500">
                    📍 Optional - used for flight planning and fuel calculation
                  </p>
                )}
              </div>

              {/* Aircraft */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Aircraft *</label>
                <select
                  value={formData.aircraft_id}
                  onChange={(e) => setFormData({ ...formData, aircraft_id: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                  required
                >
                  <option value="">Select an aircraft</option>
                  {aircraftList.map((a) => (
                    <option key={a.id} value={a.id}>{a.tail_number} – {a.manufacturer} {a.model}</option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Quantity (gallons) *</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                  required
                  min="1"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Requested Date/Time *</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Notes</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white resize-none text-base"
                  placeholder="Additional information..."
                />
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3 pt-2 pb-2">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full py-3.5 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-white font-semibold disabled:opacity-50 transition text-base"
                >
                  {submitting ? 'Sending...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={resetModal}
                  className="w-full py-3.5 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-xl text-white transition text-base"
                >
                  Cancel
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientFuelRequests;